import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Alert,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Close as CloseIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';

const parseRecordingStartMs = (isoString) => {
  if (!isoString || typeof isoString !== 'string' || !isoString.trim()) return null;
  const ms = Date.parse(isoString);
  return Number.isFinite(ms) ? ms : null;
};

/**
 * Same wall-clock format as the video scrubber (e.g. 1:00:00 PM).
 * @param {string|null|undefined} isoStart - recordings.recorded_at / start_time
 * @param {number|null|undefined} offsetSeconds
 */
export const formatWallClockExportTime = (isoStart, offsetSeconds) => {
  const baseMs = parseRecordingStartMs(isoStart);
  if (baseMs == null) return null;
  const off = offsetSeconds == null || Number.isNaN(Number(offsetSeconds)) ? 0 : Number(offsetSeconds);
  const d = new Date(baseMs + Math.max(0, off) * 1000);
  try {
    return d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  } catch {
    return null;
  }
};

/** Fallback offset format when wall clock is unavailable. */
export const formatClipTimeInput = (seconds) => {
  const raw = seconds == null || Number.isNaN(Number(seconds)) ? 0 : Math.max(0, Number(seconds));
  const totalMs = Math.round(raw * 10);
  const h = Math.floor(totalMs / 36000);
  const m = Math.floor((totalMs % 36000) / 600);
  const s = Math.floor((totalMs % 600) / 10);
  const d = totalMs % 10;
  const pad2 = (n) => String(n).padStart(2, '0');
  return `${pad2(h)}:${pad2(m)}:${pad2(s)}.${d}`;
};

/** Parse 12h wall clock (1:47:23 PM) to offset seconds from recording start. */
export const parseWallClockToOffsetSeconds = (isoStart, text, maxDurationSec) => {
  const baseMs = parseRecordingStartMs(isoStart);
  if (baseMs == null || !text) return null;

  const trimmed = String(text).trim().replace(/\u00A0/g, ' ');
  const m = trimmed.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i);
  if (!m) return null;

  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const sec = m[3] != null ? parseInt(m[3], 10) : 0;
  const ampm = m[4].toUpperCase();
  if ([h, min, sec].some((x) => Number.isNaN(x))) return null;

  if (ampm === 'PM' && h !== 12) h += 12;
  if (ampm === 'AM' && h === 12) h = 0;

  const base = new Date(baseMs);
  const target = new Date(base);
  target.setHours(h, min, sec, 0);

  let offsetSec = (target.getTime() - baseMs) / 1000;
  if (offsetSec < 0) offsetSec += 24 * 3600;
  if (!Number.isFinite(offsetSec) || offsetSec < 0) return null;

  const max = Number.isFinite(maxDurationSec) && maxDurationSec > 0 ? maxDurationSec : offsetSec;
  return Math.min(max, Math.max(0, offsetSec));
};

/** Parse HH:MM:SS.s offset or plain seconds. */
export const parseClipTimeInput = (text) => {
  const t = String(text || '').trim();
  if (!t) return null;
  if (/^\d+(\.\d+)?$/.test(t)) {
    const n = parseFloat(t);
    return Number.isFinite(n) ? Math.max(0, n) : null;
  }
  const m = t.match(/^(\d{1,2}):(\d{2}):(\d{2})(?:\.(\d))?$/) || t.match(/^(\d{1,2}):(\d{2})(?:\.(\d))?$/);
  if (!m) return null;
  if (m.length >= 5) {
    const h = parseInt(m[1], 10);
    const min = parseInt(m[2], 10);
    const sec = parseInt(m[3], 10);
    const dec = m[4] != null ? parseInt(m[4], 10) : 0;
    if ([h, min, sec, dec].some((x) => Number.isNaN(x))) return null;
    return h * 3600 + min * 60 + sec + dec / 10;
  }
  const min = parseInt(m[1], 10);
  const sec = parseInt(m[2], 10);
  const dec = m[3] != null ? parseInt(m[3], 10) : 0;
  if ([min, sec, dec].some((x) => Number.isNaN(x))) return null;
  return min * 60 + sec + dec / 10;
};

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

const pad2 = (n) => String(n).padStart(2, '0');

const parseTimeComponentsFromText = (text, useWallClock) => {
  if (useWallClock) {
    const m = String(text || '').trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?$/i);
    if (m) {
      return {
        hour: String(parseInt(m[1], 10)),
        minute: pad2(parseInt(m[2], 10)),
        second: pad2(m[3] != null ? parseInt(m[3], 10) : 0),
        ampm: (m[4] || 'AM').toUpperCase(),
      };
    }
    return { hour: '12', minute: '00', second: '00', ampm: 'AM' };
  }

  const m = String(text || '').trim().match(/^(\d{1,2}):(\d{2}):(\d{2})/);
  if (m) {
    return {
      hour: pad2(parseInt(m[1], 10)),
      minute: pad2(parseInt(m[2], 10)),
      second: pad2(parseInt(m[3], 10)),
    };
  }
  return { hour: '00', minute: '00', second: '00' };
};

const formatTimeFromComponents = (hour, minute, second, ampm, useWallClock) => {
  const min = pad2(parseInt(minute, 10) || 0);
  const sec = pad2(parseInt(second, 10) || 0);
  if (useWallClock) {
    const h = Math.min(12, Math.max(1, parseInt(hour, 10) || 12));
    return `${h}:${min}:${sec} ${ampm === 'PM' ? 'PM' : 'AM'}`;
  }
  const h = pad2(parseInt(hour, 10) || 0);
  return `${h}:${min}:${sec}.0`;
};

const ClipTimeField = ({
  label,
  value,
  onChange,
  onCommit,
  disabled,
  placeholder,
  useWallClock,
  textFieldSx,
}) => {
  const [pickerAnchor, setPickerAnchor] = useState(null);
  const [pickerHour, setPickerHour] = useState('12');
  const [pickerMinute, setPickerMinute] = useState('00');
  const [pickerSecond, setPickerSecond] = useState('00');
  const [pickerAmPm, setPickerAmPm] = useState('AM');

  const hourOptions = useMemo(() => {
    if (useWallClock) return Array.from({ length: 12 }, (_, i) => String(i + 1));
    return Array.from({ length: 24 }, (_, i) => pad2(i));
  }, [useWallClock]);

  const minuteSecondOptions = useMemo(
    () => Array.from({ length: 60 }, (_, i) => pad2(i)),
    [],
  );

  const openPicker = (e) => {
    const parts = parseTimeComponentsFromText(value, useWallClock);
    setPickerHour(parts.hour);
    setPickerMinute(parts.minute);
    setPickerSecond(parts.second);
    if (useWallClock) setPickerAmPm(parts.ampm || 'AM');
    setPickerAnchor(e.currentTarget);
  };

  const closePicker = () => setPickerAnchor(null);

  const applyPicker = () => {
    const formatted = formatTimeFromComponents(
      pickerHour,
      pickerMinute,
      pickerSecond,
      pickerAmPm,
      useWallClock,
    );
    onChange(formatted);
    closePicker();
    onCommit(formatted);
  };

  return (
    <>
      <TextField
        label={label}
        size="small"
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        fullWidth
        InputLabelProps={{ shrink: true }}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                size="small"
                aria-label={`Pick ${label.toLowerCase()}`}
                onClick={openPicker}
                disabled={disabled}
                edge="end"
                sx={{ color: 'primary.main' }}
              >
                <AccessTimeIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
        inputProps={{
          'aria-readonly': true,
          style: { cursor: 'default' },
        }}
        onKeyDown={(e) => e.preventDefault()}
        onPaste={(e) => e.preventDefault()}
        onFocus={(e) => e.target.blur()}
        sx={textFieldSx}
      />
      <Popover
        open={Boolean(pickerAnchor)}
        anchorEl={pickerAnchor}
        onClose={closePicker}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { mt: 0.5, borderRadius: 2 } } }}
      >
        <Box sx={{ p: 2, minWidth: 280 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
            Select time
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: useWallClock ? 1.5 : 2 }}>
            <FormControl size="small" sx={{ minWidth: 72, flex: 1 }}>
              <InputLabel>Hour</InputLabel>
              <Select
                value={pickerHour}
                label="Hour"
                onChange={(e) => setPickerHour(e.target.value)}
              >
                {hourOptions.map((h) => (
                  <MenuItem key={h} value={h}>
                    {h}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography sx={{ fontWeight: 700, color: 'text.secondary' }}>:</Typography>
            <FormControl size="small" sx={{ minWidth: 72, flex: 1 }}>
              <InputLabel>Min</InputLabel>
              <Select
                value={pickerMinute}
                label="Min"
                onChange={(e) => setPickerMinute(e.target.value)}
              >
                {minuteSecondOptions.map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography sx={{ fontWeight: 700, color: 'text.secondary' }}>:</Typography>
            <FormControl size="small" sx={{ minWidth: 72, flex: 1 }}>
              <InputLabel>Sec</InputLabel>
              <Select
                value={pickerSecond}
                label="Sec"
                onChange={(e) => setPickerSecond(e.target.value)}
              >
                {minuteSecondOptions.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          {useWallClock && (
            <ToggleButtonGroup
              value={pickerAmPm}
              exclusive
              size="small"
              fullWidth
              onChange={(_, next) => {
                if (next) setPickerAmPm(next);
              }}
              sx={{ mb: 2 }}
            >
              <ToggleButton value="AM" sx={{ flex: 1, textTransform: 'none' }}>
                AM
              </ToggleButton>
              <ToggleButton value="PM" sx={{ flex: 1, textTransform: 'none' }}>
                PM
              </ToggleButton>
            </ToggleButtonGroup>
          )}
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="small" onClick={closePicker} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={applyPicker}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Apply
            </Button>
          </Stack>
        </Box>
      </Popover>
    </>
  );
};

const formatExportTimeDisplay = (isoStart, offsetSeconds, useWallClock) => {
  if (useWallClock) {
    const wc = formatWallClockExportTime(isoStart, offsetSeconds);
    if (wc) return wc;
  }
  return formatClipTimeInput(offsetSeconds);
};

const parseExportTimeInput = (isoStart, text, maxDurationSec, useWallClock) => {
  if (useWallClock) {
    const wc = parseWallClockToOffsetSeconds(isoStart, text, maxDurationSec);
    if (wc != null) return wc;
  }
  return parseClipTimeInput(text);
};

/**
 * Range selection bar + export controls — wall-clock times match the video scrubber.
 */
export function RecordingDownloadPanel({
  durationSeconds = 0,
  currentTime = 0,
  startSeconds = 0,
  endSeconds = 0,
  recordingStartIso = null,
  onStartChange,
  onEndChange,
  onExport,
  onClose,
  disabled = false,
}) {
  const useWallClock = parseRecordingStartMs(recordingStartIso) != null;

  const [startText, setStartText] = useState('00:00:00.0');
  const [endText, setEndText] = useState('00:00:00.0');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const barRef = useRef(null);
  const dragRef = useRef(null);

  const dur = Number.isFinite(durationSeconds) && durationSeconds > 0 ? durationSeconds : 0;

  useEffect(() => {
    setStartText(formatExportTimeDisplay(recordingStartIso, startSeconds, useWallClock));
  }, [startSeconds, recordingStartIso, useWallClock]);

  useEffect(() => {
    setEndText(formatExportTimeDisplay(recordingStartIso, endSeconds, useWallClock));
  }, [endSeconds, recordingStartIso, useWallClock]);

  const pct = (sec) => (dur > 0 ? (sec / dur) * 100 : 0);

  const commitStartText = useCallback((overrideText) => {
    const text = overrideText != null ? overrideText : startText;
    const parsed = parseExportTimeInput(recordingStartIso, text, dur, useWallClock);
    if (parsed == null) {
      setStartText(formatExportTimeDisplay(recordingStartIso, startSeconds, useWallClock));
      return;
    }
    const maxStart = Math.max(0, endSeconds - 0.1);
    onStartChange(clamp(parsed, 0, maxStart));
  }, [startText, startSeconds, endSeconds, onStartChange, recordingStartIso, dur, useWallClock]);

  const commitEndText = useCallback((overrideText) => {
    const text = overrideText != null ? overrideText : endText;
    const parsed = parseExportTimeInput(recordingStartIso, text, dur, useWallClock);
    if (parsed == null) {
      setEndText(formatExportTimeDisplay(recordingStartIso, endSeconds, useWallClock));
      return;
    }
    const minEnd = Math.min(dur, startSeconds + 0.1);
    onEndChange(clamp(parsed, minEnd, dur || parsed));
  }, [endText, endSeconds, startSeconds, dur, onEndChange, recordingStartIso, useWallClock]);

  const handleBarPointerDown = useCallback(
    (e, handle) => {
      if (disabled || dur <= 0) return;
      e.preventDefault();
      dragRef.current = handle;

      const onMove = (ev) => {
        const bar = barRef.current;
        if (!bar || !dragRef.current) return;
        const rect = bar.getBoundingClientRect();
        const ratio = clamp((ev.clientX - rect.left) / rect.width, 0, 1);
        const t = ratio * dur;
        if (dragRef.current === 'start') {
          onStartChange(clamp(t, 0, endSeconds - 0.1));
        } else if (dragRef.current === 'end') {
          onEndChange(clamp(t, startSeconds + 0.1, dur));
        } else {
          const half = (endSeconds - startSeconds) / 2;
          const newStart = clamp(t - half, 0, dur - (endSeconds - startSeconds));
          onStartChange(newStart);
          onEndChange(newStart + (endSeconds - startSeconds));
        }
      };

      const onUp = () => {
        dragRef.current = null;
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
      };

      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', onUp);
    },
    [disabled, dur, startSeconds, endSeconds, onStartChange, onEndChange],
  );

  const handleExport = async () => {
    if (disabled || exporting || dur <= 0) return;
    setExportError(null);
    setExporting(true);
    try {
      await onExport(startSeconds, endSeconds);
    } catch (err) {
      setExportError(err?.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const wallStart = formatWallClockExportTime(recordingStartIso, startSeconds);
  const wallEnd = formatWallClockExportTime(recordingStartIso, endSeconds);

  const selectionLabel = useWallClock && wallStart && wallEnd
    ? `${wallStart} — ${wallEnd}`
    : dur > 0
      ? `${formatClipTimeInput(endSeconds - startSeconds)} selected`
      : '—';

  const timePlaceholder = useWallClock ? '1:00:00 PM' : '00:00:00.0';

  const timeFieldSx = {
    '& .MuiOutlinedInput-root': {
      bgcolor: 'grey.50',
      '& fieldset': { borderColor: 'grey.400', borderWidth: 1.5 },
      '&:hover fieldset': { borderColor: 'primary.main' },
      '&.Mui-focused fieldset': { borderWidth: 2 },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
  };

  return (
    <Box
      sx={{
        width: { xs: '100%', md: 280 },
        flexShrink: 0,
        borderLeft: { md: '1px solid' },
        borderColor: { md: 'divider' },
        pl: { md: 2.5 },
        pt: { xs: 2, md: 0 },
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
            Export clip
          </Typography>
          {useWallClock && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
              Times match the video player clock
            </Typography>
          )}
        </Box>
        {onClose && (
          <IconButton
            aria-label="Close export clip panel"
            onClick={onClose}
            size="small"
            sx={{
              mt: -0.5,
              mr: -0.5,
              color: 'text.secondary',
              '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Box
        sx={{
          borderRadius: 1.5,
          bgcolor: 'grey.900',
          border: '1px solid',
          borderColor: 'grey.800',
          px: 1.5,
          py: 1.25,
        }}
      >
        <Box
          ref={barRef}
          sx={{
            position: 'relative',
            height: 44,
            borderRadius: 1,
            bgcolor: 'grey.700',
            boxShadow: 'inset 0 1px 4px rgba(0,0,0,0.5)',
            cursor: disabled ? 'default' : 'pointer',
            overflow: 'visible',
          }}
          onPointerDown={(e) => {
            if (disabled || dur <= 0) return;
            const rect = barRef.current.getBoundingClientRect();
            const ratio = clamp((e.clientX - rect.left) / rect.width, 0, 1);
            const t = ratio * dur;
            if (Math.abs(t - startSeconds) <= Math.abs(t - endSeconds)) {
              onStartChange(clamp(t, 0, endSeconds - 0.1));
            } else {
              onEndChange(clamp(t, startSeconds + 0.1, dur));
            }
          }}
        >
          {dur > 0 && (
            <>
              {pct(startSeconds) > 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: 0,
                    width: `${pct(startSeconds)}%`,
                    top: 0,
                    bottom: 0,
                    bgcolor: 'rgba(0,0,0,0.4)',
                    borderRadius: '4px 0 0 4px',
                    pointerEvents: 'none',
                  }}
                />
              )}
              {pct(endSeconds) < 100 && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: `${pct(endSeconds)}%`,
                    right: 0,
                    top: 0,
                    bottom: 0,
                    bgcolor: 'rgba(0,0,0,0.4)',
                    borderRadius: '0 4px 4px 0',
                    pointerEvents: 'none',
                  }}
                />
              )}
              <Box
                sx={{
                  position: 'absolute',
                  left: `${pct(startSeconds)}%`,
                  width: `${pct(endSeconds) - pct(startSeconds)}%`,
                  top: 0,
                  bottom: 0,
                  bgcolor: 'primary.main',
                  boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.2)',
                  pointerEvents: 'none',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  left: `${pct(currentTime)}%`,
                  top: 2,
                  bottom: 2,
                  width: 3,
                  bgcolor: 'warning.light',
                  borderRadius: 1,
                  boxShadow: '0 0 8px rgba(251,191,36,0.85)',
                  transform: 'translateX(-50%)',
                  pointerEvents: 'none',
                  zIndex: 1,
                }}
              />
              {['start', 'end'].map((handle) => {
                const sec = handle === 'start' ? startSeconds : endSeconds;
                return (
                  <Box
                    key={handle}
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      handleBarPointerDown(e, handle);
                    }}
                    title={handle === 'start' ? 'Start' : 'End'}
                    sx={{
                      position: 'absolute',
                      left: `${pct(sec)}%`,
                      top: '50%',
                      width: 12,
                      height: 32,
                      transform: 'translate(-50%, -50%)',
                      cursor: disabled ? 'default' : 'col-resize',
                      zIndex: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 1,
                      bgcolor: 'common.white',
                      border: '2px solid',
                      borderColor: 'primary.light',
                      boxShadow: '0 2px 10px rgba(0,0,0,0.45)',
                      '&::after': {
                        content: '""',
                        width: 2,
                        height: 16,
                        borderRadius: 1,
                        bgcolor: 'primary.main',
                        boxShadow: '4px 0 0 0 rgba(64, 81, 138, 1)',
                      },
                    }}
                  />
                );
              })}
            </>
          )}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
          <Typography
            variant="caption"
            sx={{
              color: 'grey.300',
              fontWeight: 600,
              fontFamily: useWallClock ? 'inherit' : 'monospace',
              letterSpacing: '0.02em',
            }}
          >
            {wallStart || 'Start'}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'grey.300',
              fontWeight: 600,
              fontFamily: useWallClock ? 'inherit' : 'monospace',
              letterSpacing: '0.02em',
            }}
          >
            {wallEnd || 'End'}
          </Typography>
        </Box>
      </Box>

      <Stack spacing={1.5}>
        <ClipTimeField
          label="Start Time"
          value={startText}
          onChange={setStartText}
          onCommit={commitStartText}
          disabled={disabled || dur <= 0}
          placeholder={timePlaceholder}
          useWallClock={useWallClock}
          textFieldSx={timeFieldSx}
        />
        <ClipTimeField
          label="End Time"
          value={endText}
          onChange={setEndText}
          onCommit={commitEndText}
          disabled={disabled || dur <= 0}
          placeholder={timePlaceholder}
          useWallClock={useWallClock}
          textFieldSx={timeFieldSx}
        />
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ mt: -0.5 }}>
        {selectionLabel} · MKV format
      </Typography>

      {exportError && (
        <Alert severity="error" onClose={() => setExportError(null)} sx={{ py: 0 }}>
          {exportError}
        </Alert>
      )}

      <Stack spacing={1}>
        <Button
          variant="contained"
          startIcon={exporting ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
          onClick={handleExport}
          disabled={disabled || exporting || dur <= 0 || endSeconds <= startSeconds}
          fullWidth
          sx={{ textTransform: 'none', fontWeight: 600, py: 1.1 }}
        >
          {exporting ? 'Exporting…' : 'Export Clip'}
        </Button>
        <Button
          variant="text"
          size="small"
          onClick={() => {
            onStartChange(0);
            onEndChange(dur);
          }}
          disabled={disabled || dur <= 0}
          sx={{ textTransform: 'none', alignSelf: 'center' }}
        >
          Select full recording
        </Button>
      </Stack>
    </Box>
  );
}

export default RecordingDownloadPanel;
